import { FormControlLabel, RadioGroup } from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import { fromPairs } from "lodash";
import * as React from "react";
import { useParams } from "react-router";
import { t } from "../../../i18n/util";
import { API } from "../../../network/API";
import { GetAppointmentsResponseAppointments } from "../../../network/APITypes";
import { generalStore } from "../../../stores/GeneralStore";
import { THEME, isDal } from "../../../theme/ThemeStore";
import { ConfirmationDialog } from "../../ui/ConfirmationDialog";
import { ContentWrapper } from "../../ui/ContentWrapper";
import { CustomRadio } from "../../ui/CustomRadio";
import { ErrorMessagesList, HrBotButton, ImageLogo } from "../../ui/Primitives";
import { ApplicantIdParam, getFormattedTimeRange } from "../../util/helpers";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";

const dateToLocation = (date: GetAppointmentsResponseAppointments) => {
    if (date.onlineMeeting) {
        return t("screen.appointmentConfirmation.onlineMeeting");
    } else {
        if (THEME.THEME_NAME === "aaa") {
            return `${t("screen.success.aaa.address.street")} ${t("screen.success.aaa.address.zip")}`;
        } else if (THEME.THEME_NAME === "dal") {
            return `${t("screen.success.dal.address.street")} ${t("screen.success.dal.address.zip")}`;
        }
    }
};

export const AppointmentConfirmationSite = () => {
    const { applicantId } = useParams<ApplicantIdParam>();

    // check if user has already confirmed appointments
    const [completed, setCompleted] = React.useState<boolean | undefined>(undefined);

    // appointments
    const [appointments, setAppointments] = React.useState<GetAppointmentsResponseAppointments[] | undefined>();

    // variable used for toggles of form
    const [appointmentValues, setAppointmentValues] = React.useState<{ [key: string]: boolean }>();

    const [currentAppointment, setCurrentAppointment] = React.useState("");

    // handle opening of Dialog
    const [openDialog, setOpenDialog] = React.useState(false);

    const isMobile = useMobileMediaQuery();

    const loadAppointments = React.useCallback(async () => {
        try {
            // load dates and check if already confirmed
            const res = await API.getAppointments(applicantId);

            setAppointments(res.appointments);
            if (res.appointments.some((appointment) => appointment.preferred) || res.appointments.length === 0) {
                setCompleted(true);
            } else {
                setAppointmentValues(fromPairs((res?.appointments).map((appointment) => [appointment.id, false])));
            }
        } catch (err) {
            generalStore.setError(t("error.loadAppointments"), err);
        }
    }, [applicantId]);

    React.useEffect(() => {
        loadAppointments();
    }, [loadAppointments]);

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSubmitDialog = async () => {
        try {
            if (!selectedAppointment) {
                return;
            }
            await API.postAppointments(applicantId, [selectedAppointment.id]);

            await loadAppointments();

            setOpenDialog(false);
        } catch (error) {
            generalStore.setError(t("error.confirmAppointments"), error);
        }
    };

    const selectedAppointment = appointments?.find((appointment) => appointment.id === currentAppointment);

    const preferredAppointment = appointments?.find((appointment) => appointment.preferred);

    return (
        <>
            <div style={{ padding: "29px 18px" }}>
                {!isDal() && <ImageLogo style={{ width: "auto", height: 37 }} />}
                <ContentWrapper>
                    {isDal() && (
                        <div style={{ marginBottom: 32 }}>
                            <ImageLogo style={{ width: "auto", height: 37 }} />
                        </div>
                    )}
                    {completed ? (
                        <>
                            <h2
                                style={{
                                    color: THEME.customColors.body1Dark,
                                    fontWeight: 400,
                                    fontSize: 28,
                                    marginBottom: 42
                                }}
                            >
                                {t("screen.appointmentConfirmation.success.header")}
                            </h2>

                            <p style={{ marginBottom: 32 }}>
                                {t("screen.appointmentConfirmation.success.text")}
                                <br />
                                <br />
                                {t("screen.appointmentConfirmation.success.text2")}
                            </p>
                            <p className="body1" style={{ marginBottom: 10, color: isDal() ? "#9b9b9b" : undefined }}>
                                {getFormattedTimeRange(preferredAppointment?.startTime, preferredAppointment?.endTime)}
                                <br />
                                {preferredAppointment && dateToLocation(preferredAppointment)}
                            </p>
                            <div
                                style={{
                                    width: "100%",
                                    height: 2,
                                    backgroundColor: THEME.customColors.body1Dark,
                                    opacity: 0.1,
                                    marginBottom: 32,
                                    marginTop: 80
                                }}
                            />
                            <div
                                style={{
                                    color: THEME.customColors.body1Dark,
                                    opacity: 0.2,
                                    marginBottom: 16,
                                    fontSize: 14
                                }}
                            >
                                {t(isDal() ? "screen.codeExample.footer.dal" : "screen.codeExample.footer")}
                            </div>
                        </>
                    ) : (
                        <>
                            <h2
                                style={{
                                    color: THEME.customColors.body1Dark,
                                    fontWeight: 400,
                                    fontSize: 28,
                                    marginBottom: 42
                                }}
                            >
                                {t("screen.codeExample.dearApplicant")}
                            </h2>
                            <p style={{ marginBottom: 32 }}>
                                {t("screen.appointmentConfirmation.text", {
                                    admin: `${appointments?.[0]?.createdBy?.firstName} ${appointments?.[0]?.createdBy?.lastName}`,
                                    title: appointments?.[0]?.title
                                })}
                            </p>
                            {appointmentValues && (
                                <Formik
                                    initialValues={appointmentValues}
                                    onSubmit={() => {
                                        setOpenDialog(true);
                                    }}
                                    validateOnChange
                                    validateOnBlur
                                    validate={(values) => {
                                        // Check if a radio button is selected
                                        if (!currentAppointment) {
                                            // Add error to last radio button
                                            const keys = Object.keys(values);
                                            const lastKey = keys[keys.length - 1];
                                            return { [lastKey]: t("error.appointmentConfirmation") };
                                        }
                                    }}
                                >
                                    {({ errors, touched }) => (
                                        <Form style={{ height: "100%", flexGrow: 1 }}>
                                            <RadioGroup
                                                name="appointments"
                                                value={currentAppointment}
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: isMobile ? 8 : 0
                                                }}
                                            >
                                                {appointments?.map((appointment) => (
                                                    <FormControlLabel
                                                        key={appointment.id}
                                                        control={
                                                            <Field
                                                                type="radio"
                                                                component={CustomRadio}
                                                                name="appointment"
                                                            />
                                                        }
                                                        label={`${getFormattedTimeRange(
                                                            appointment.startTime,
                                                            appointment.endTime
                                                        )} ${dateToLocation(appointment)}`}
                                                        value={appointment.id}
                                                        onClick={() => setCurrentAppointment(appointment.id)}
                                                    />
                                                ))}
                                            </RadioGroup>
                                            <div style={{ marginTop: 10, minHeight: 30 }}>
                                                <ErrorMessagesList errors={errors} touched={touched} />
                                            </div>

                                            <HrBotButton
                                                label={t("screen.appointmentConfirmation.confirmAppointment")}
                                                style={{ alignSelf: "flex-start", marginBottom: 40, marginTop: 40 }}
                                                type="submit"
                                            />
                                        </Form>
                                    )}
                                </Formik>
                            )}
                            <div
                                style={{
                                    width: "100%",
                                    height: 2,
                                    backgroundColor: THEME.customColors.body1Dark,
                                    opacity: 0.1,
                                    marginBottom: 32
                                }}
                            />
                            <div
                                style={{
                                    color: THEME.customColors.body1Dark,
                                    opacity: 0.2,
                                    marginBottom: 16,
                                    fontSize: 14
                                }}
                            >
                                {t(isDal() ? "screen.codeExample.footer.dal" : "screen.codeExample.footer")}
                            </div>
                        </>
                    )}
                </ContentWrapper>
            </div>

            <ConfirmationDialog
                isOpen={openDialog}
                onBack={handleCloseDialog}
                onSubmit={handleSubmitDialog}
                header={t("screen.appointmentConfirmation.confirmAppointment")}
                submitButtonText={t("screen.dashboard.confirm")}
                label={
                    <div style={{ textAlign: "left" }}>
                        <p style={{ marginBottom: 20 }}>{t("screen.appointmentConfirmation.confirmDialog.text")}</p>
                        <p style={{ marginTop: 10 }}>
                            {getFormattedTimeRange(selectedAppointment?.startTime, selectedAppointment?.endTime)}
                            <br />
                            {selectedAppointment && dateToLocation(selectedAppointment)}
                        </p>
                    </div>
                }
            />
        </>
    );
};
